'use client';

import Link from 'next/link';
import { Clock, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDashboardNumber } from './analytics-dashboard-style';

type AttentionBlock = {
  count: number;
  items: Array<{
    id: number;
    employee_name?: string;
    location_name?: string;
    hours_overdue?: number;
  }>;
};

type HealthOverviewShifts = {
  open_past_scheduled_end?: AttentionBlock;
  open_past_location_close?: AttentionBlock;
  scheduled_today_no_start?: AttentionBlock;
  open_shifts_gt_12h?: number;
};

export function ShiftsAttentionWidget() {
  const [data, setData] = useState<HealthOverviewShifts | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.getAnalyticsHealthOverview();
      setData(result as HealthOverviewShifts);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const pastEnd = data?.open_past_scheduled_end?.count ?? 0;
  const pastClose = data?.open_past_location_close?.count ?? 0;
  const noStart = data?.scheduled_today_no_start?.count ?? 0;
  const gt12 = data?.open_shifts_gt_12h ?? 0;
  const total = pastEnd + pastClose + noStart + (gt12 > 0 ? 1 : 0);

  return (
    <Card className="rounded-3xl border-[var(--app-border)] bg-[var(--app-surface)] shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Clock className="h-4 w-4 text-amber-600" />
          Смены: нужно внимание
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-[var(--app-text-soft)]">
            <Loader2 className="h-4 w-4 animate-spin" />
            Загрузка…
          </div>
        ) : total === 0 ? (
          <p className="rounded-2xl border border-emerald-200 bg-emerald-600 px-4 py-4 text-sm text-white">
            Просроченных открытых смен и «не вышли по графику» сейчас не видно.
          </p>
        ) : (
          <div className="grid gap-2.5 sm:grid-cols-2">
            {pastEnd > 0 ? (
              <Link
                href="/shifts/list?status=open"
                className="rounded-2xl border border-amber-200 bg-amber-700 p-3 transition hover:shadow-sm"
              >
                <p className="text-2xl font-bold text-amber-900">
                  {formatDashboardNumber(pastEnd)}
                </p>
                <p className="mt-1 text-sm font-semibold text-[var(--app-text)]">
                  Открыты после конца смены
                </p>
                <p className="mt-0.5 text-xs text-[var(--app-text-soft)]">
                  По расписанию смена уже должна быть закрыта
                </p>
              </Link>
            ) : null}
            {pastClose > 0 ? (
              <Link
                href="/shifts/list?status=open"
                className="rounded-2xl border border-orange-700 bg-orange-700 p-3 text-white transition hover:shadow-sm"
              >
                <p className="text-2xl font-bold text-orange-900">
                  {formatDashboardNumber(pastClose)}
                </p>
                <p className="mt-1 text-sm font-semibold text-[var(--app-text)]">
                  Открыты после закрытия точки
                </p>
                <p className="mt-0.5 text-xs text-[var(--app-text-soft)]">
                  По графику точки рабочий день уже закончился
                </p>
              </Link>
            ) : null}
            {noStart > 0 ? (
              <Link
                href="/shifts/list?status=scheduled"
                className="rounded-2xl border border-sky-700 bg-sky-700 p-3 text-white transition hover:shadow-sm"
              >
                <p className="text-2xl font-bold text-sky-900 dark:text-sky-100">
                  {formatDashboardNumber(noStart)}
                </p>
                <p className="mt-1 text-sm font-semibold text-[var(--app-text)]">
                  Запланированы, не открыли
                </p>
                <p className="mt-0.5 text-xs text-[var(--app-text-soft)]">
                  Смена на сегодня без факта открытия
                </p>
              </Link>
            ) : null}
            {gt12 > 0 ? (
              <Link
                href="/shifts/list?status=open"
                className="rounded-2xl border border-rose-200 bg-rose-600 p-3 transition hover:shadow-sm"
              >
                <p className="text-2xl font-bold text-rose-900">
                  {formatDashboardNumber(gt12)}
                </p>
                <p className="mt-1 text-sm font-semibold text-[var(--app-text)]">
                  Открыты более 12 часов
                </p>
              </Link>
            ) : null}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
